import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import SkillsInput from '../common/SkillsInput';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { useUser } from '../../context/UserContext';
import PageTabs from '../shared/PageTabs';
import { buildLPathTabs } from '../shared/tabConfigs';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material';
import {
  Upload,
  Close,
} from '@mui/icons-material';
import { learningPathService } from '../../services/api';
const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const DropzoneArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100]
  }
}));

const PreviewImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '200px',
  objectFit: 'cover',
  borderRadius: '8px'
});

const modules = {
  toolbar: {
    container: [
      [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['clean']
    ]
  },
  clipboard: {
    matchVisual: false
  }
};
const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video'
];

const LPathForm = () => {
  const { id } = useParams();

  const { user } = useUser();
  const permissions = user?.permissions || [];

  const lpathTabs = buildLPathTabs(id, { permissions: user?.permissions || [], roleId: user?.role_id });  

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [formData, setFormData] = useState({
    lpath_title: '',
    description: '',
    lpath_status: '1',
    content_ordered: '0',
    skills: [],
    audience_type: '3'// 1=Internal, 2=External, 3=Both
  });
  const [preview, setPreview] = useState('');

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setFormData(prev => ({
          ...prev,
          cover_picture: file
        }));
        setPreview(URL.createObjectURL(file));
      }
    },
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1
  });

  useEffect(() => {
    if (id) {
      loadLPathData();
    }
  }, [id]);

  const loadLPathData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await learningPathService.getLPathById(id);
      setFormData({
        lpath_title: data.record.title,
        description: data.record.description,
        lpath_status: data.record.status,
        content_ordered: data.record.content_ordered || '0',
        skills: data.record.skills || [],
        audience_type: data.record.audience_type || '3'
      });
      if (data.record.cover_picture) {
        setPreview(`/uploads/learning_path/${data.record.cover_picture}`);
      }
    } catch (error) {
      console.error('Error loading learning path:', error);
      setLoadError(error?.response?.data?.message || error.message || 'Unable to load learning path');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };
  //Validation
  const [errors, setErrors] = useState({});
  const validateForm = () => {
    const newErrors = {};
    if (!formData.lpath_title.trim()) {
      newErrors.lpath_title = 'Learning Path name is required';
    }

    if (!stripHtml(formData.description).trim()) {
      newErrors.description = 'Learning Path description is required';
    }

    //if (!formData.cover_picture && !preview) {
    //  newErrors.cover_picture = 'Learning Path cover image is required';
    //}

    if (!formData.audience_type) {
      newErrors.audience_type = 'Please select a target audience';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const learningPathPayload = {
        ...formData,
        skills: JSON.stringify((formData.skills || []).map((skill) => skill && typeof skill === 'object' ? skill.id : skill)),
      };
      const response = id
        ? await learningPathService.updatelpath(id, learningPathPayload)
        : await learningPathService.createLPath(learningPathPayload);

      setSnackbar({
        open: true,
        message: 'Learning Path saved successfully!',
        severity: 'success'
      });
      setTimeout(() => {
        navigate(`/learning_path/settings/${response.data.lpath_id}`);
      }, 1000);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Error saving Learning Path';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ pt:2 }}>
      {id && <PageTabs tabs={lpathTabs} />}
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mt: 7 }}
        >
          <MuiAlert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
        
        <Typography variant="h4" sx={{ mb: 4 }}>
          {id ? 'Edit Learning Path' : 'Create New Learning Path'}
        </Typography>
        {loadError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" action={/* One outlined style (K2.3 pt 15). Removed 3 Nov 2026 (K2.3 pt 15): a text button. */ <Button variant="outlined" size="small" onClick={loadLPathData}>Retry</Button>}>
              {loadError}
            </Alert>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <FormSection>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Basic Information
            </Typography>
            <TextField
              fullWidth
              label="Learning Path Name"
              name="lpath_title"
              value={formData.lpath_title}
              onChange={handleChange}
              variant="outlined"
              sx={{ mb: 3 }}
              error={!!errors.lpath_title}
              helperText={errors.lpath_title}
            />
          </FormSection>
          <FormSection>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Learning Path Description
            </Typography>
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={(content) => setFormData(prev => ({
                ...prev,
                description: content
              }))}
              style={{ height: '200px', marginBottom: '50px' }}
              modules={modules}
              formats={formats}
            />
            {errors.description && (
              <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                {errors.description}
              </Typography>
            )}
          </FormSection>
          {/*
          <Typography variant="h6" sx={{ mb: 2 }}>
            Cover Image
          </Typography>
          <DropzoneArea {...getRootProps()}>
            <input {...getInputProps()} />
            {preview ? (
              <Box position="relative">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview('');
                    setFormData(prev => ({ ...prev, cover_picture: null }));
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'white',
                    '&:hover': { backgroundColor: 'white' }
                  }}
                >
                  <Close />
                </IconButton>
                <PreviewImage src={preview} alt="Cover preview" />
              </Box>
            ) : (
              <Box>
                <Upload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Drag and drop your cover image
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Supported formats: Images [.jpeg, .jpg, .png]
                </Typography>
              </Box>
            )}
          </DropzoneArea>
          {errors.cover_picture && (
            <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
              {errors.cover_picture}
            </Typography>
          )}
          */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Sequencing of Courses
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Learners must complete courses in order when enabled.
              </Typography>
            </Box>
            <RadioGroup
              row
              name="content_ordered"
              value={formData.content_ordered}
              onChange={handleChange}
            >
              {/* N44 pt 9.1: each option saves what its label says; the course and path pages read '1' as ordered.
                  Removed 3 Nov 2026 (N44 pt 9.1): 'Ordered' on '0' and 'Unordered' on '1', the wrong way round. */}
              <FormControlLabel
                value="1"
                control={<Radio />}
                label="Ordered"
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold', color: 'text.primary' } }}
              />
              <FormControlLabel
                value="0"
                control={<Radio />}
                label="Unordered"
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold', color: 'text.primary' } }}
              />
            </RadioGroup>
          </Box>

          {id && (
            <FormSection sx={{ mt: 3 }}>
              <SkillsInput
                value={formData.skills}
                onChange={(newSkills) =>
                  setFormData((prev) => ({
                    ...prev,
                    skills: newSkills,
                  }))
                }
                error={!!errors.skills}
                helperText={errors.skills}
                required={false}
              />
            </FormSection>
          )}
          <FormSection sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Target Audience
            </Typography>
            <RadioGroup
              row
              name="audience_type"
              value={formData.audience_type}
              onChange={handleChange}
            >
              <FormControlLabel 
                value="1" 
                control={<Radio />} 
                label="Internal Learners"
                disabled={!!id}
              />
              <FormControlLabel 
                value="2" 
                control={<Radio />} 
                label="External Learners"
                disabled={!!id}
              />
              <FormControlLabel 
                value="3" 
                control={<Radio />} 
                label="Both"
                disabled={!!id}
              />
            </RadioGroup>
            {errors.audience_type && (
              <Typography color="error" variant="caption" display="block">
                {errors.audience_type}
              </Typography>
            )}
          </FormSection>

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2
            }}
          >
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/learning_path')}
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              className="add-course-btn"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default LPathForm;